import { useRef, useState } from "react";
import { useI18n } from "./i18n.jsx";
import "./tree-move.css";

export function StructureTree({ node, selectedId, onSelect, enabled, canDrop, onMove, icons }) {
  const { t } = useI18n();
  const [dragId, setDragId] = useState(null);
  const [drop, setDrop] = useState(null);
  const suppressClickUntil = useRef(0);
  function end() { setDragId(null); setDrop(null); suppressClickUntil.current = Date.now() + 300; }
  function destination(event, target) {
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientY - rect.top) / rect.height;
    const placement = fraction < .25 ? "before" : fraction > .75 ? "after" : "inside";
    return { nodeId: dragId, targetId: target.id, placement };
  }
  const interaction = {
    start(event, id) {
      if (!enabled) { event.preventDefault(); return; }
      event.stopPropagation(); setDragId(id);
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("application/x-twinscope-node", id);
    },
    over(event, target) {
      event.stopPropagation();
      if (!enabled || !dragId) return;
      const next = destination(event, target);
      if (!canDrop(next)) { event.dataTransfer.dropEffect = "none"; setDrop(null); return; }
      event.preventDefault(); event.dataTransfer.dropEffect = "move";
      setDrop(previous => previous?.targetId === next.targetId && previous?.placement === next.placement ? previous : next);
    },
    finish(event, target) {
      event.preventDefault(); event.stopPropagation();
      const next = destination(event, target);
      if (enabled && dragId && canDrop(next)) onMove(next);
      end();
    },
    leave(event) { if (!event.currentTarget.contains(event.relatedTarget)) setDrop(null); },
    end,
    select(id) { if (Date.now() >= suppressClickUntil.current && !dragId) onSelect(id); },
  };
  return <div className="structure-tree">
    {enabled && <p className="tree-drag-help">{t("treeDragHelp")}</p>}
    <Branch node={node} selectedId={selectedId} enabled={enabled} icons={icons}
      interaction={interaction} dragId={dragId} drop={enabled ? drop : null} depth={0} />
    {enabled && drop && <span className="tree-drop-hint" role="status">{t("treeDrop_" + drop.placement)}</span>}
  </div>;
}

function Branch({ node, selectedId, enabled, icons, interaction, dragId, drop, depth }) {
  const [open, setOpen] = useState(true);
  const hasChildren = Boolean(node.children?.length);
  return <div className="tree-branch">
    <button type="button"
      className={`tree-row ${selectedId === node.id ? "selected" : ""} ${dragId === node.id ? "dragging" : ""} ${drop?.targetId === node.id ? "drop-" + drop.placement : ""}`}
      style={{ "--depth": depth }} draggable={enabled && depth > 0}
      onClick={() => interaction.select(node.id)}
      onDragStart={event => interaction.start(event, node.id)} onDragEnd={interaction.end}
      onDragOver={event => interaction.over(event, node)}
      onDragLeave={interaction.leave} onDrop={event => interaction.finish(event, node)}>
      <i onClick={event => { event.stopPropagation(); setOpen(value => !value); }}>{hasChildren ? (open ? "⌄" : "›") : ""}</i>
      <b>{icons[node.type]}</b><span>{node.name}</span><em className={node.status} />
    </button>
    {open && node.children?.map(child => <Branch key={child.id} node={child} selectedId={selectedId}
      enabled={enabled} icons={icons} interaction={interaction} dragId={dragId} drop={drop} depth={depth + 1} />)}
  </div>;
}
